import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LeadCloudFileType } from '@econtractor/types';
import {
    createCloudflareFolder,
    deleteCloudflareFiles,
    deleteCloudflareFolder,
    getCloudflareUploadUrls,
    linkCloudflareFiles,
    moveCloudflareItem,
    renameCloudflareItem,
    type CloudUploadScope,
} from 'api/apiCloudflare';
import { useToast } from 'context/ToastContext';
import { toStoredCloudFile, withLockedExtension } from '../helper/filesUtils';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { getGalleryId, getScopeEntityId } from './cloudUploadScope';
import {
    FILE_NAME_MAX,
    FOLDER_PATH_MAX,
    isValidFolderName,
    joinFolderPath,
    isCloudFolder,
    isPathInsideFolder,
    itemFolderPath,
    listCurrentFiles,
    listCurrentFolders,
    nearestExistingPath,
    normalizeFolderPath,
    parentFolderPath,
    replaceFolderPathPrefix,
    siblingNamesAtPath,
} from './folderTree';

interface UseFilesContainerParams {
    files: LeadCloudFileType[];
    scope: CloudUploadScope;
    saveFirstMessage: string;
    errorMessage: string;
    uploadSuccessMessage: string;
    folderCreateSuccessMessage: string;
    folderDuplicateMessage: string;
    folderInvalidNameMessage: string;
    renameSuccessMessage: string;
    moveSuccessMessage: string;
    onFilesChange: (nextFiles: LeadCloudFileType[]) => Promise<boolean>;
    onEnsureEntity?: () => Promise<CloudUploadScope | false>;
    noFolder?: boolean;
}

const useFilesContainer = ({
    files,
    scope,
    saveFirstMessage,
    errorMessage,
    uploadSuccessMessage,
    folderCreateSuccessMessage,
    folderDuplicateMessage,
    folderInvalidNameMessage,
    renameSuccessMessage,
    moveSuccessMessage,
    onFilesChange,
    onEnsureEntity,
    noFolder = false,
}: UseFilesContainerParams) => {
    const { showToast } = useToast();
    const [progress, setProgress] = useState<number | false>(false);
    const [ensuring, setEnsuring] = useState(false);
    const [deletingKey, setDeletingKey] = useState<string | null>(null);
    const [renamingKey, setRenamingKey] = useState<string | null>(null);
    const [movingKey, setMovingKey] = useState<string | null>(null);
    const [currentPath, setCurrentPath] = useState('');
    const isBusy = progress !== false || ensuring;
    const refInputFile = useRef<HTMLInputElement>(null);
    const scopeRef = useRef(scope);

    const entityId = getScopeEntityId(scope);
    const entityIdRef = useRef(entityId);

    useEffect(() => {
        const incomingId = getScopeEntityId(scope);
        const currentId = getScopeEntityId(scopeRef.current);
        if (incomingId > 0 || currentId <= 0) {
            scopeRef.current = scope;
        }
    }, [scope]);

    useEffect(() => {
        if (noFolder) {
            setCurrentPath('');
            return;
        }
        setCurrentPath((prev) => {
            if (entityIdRef.current !== entityId) {
                entityIdRef.current = entityId;
                return '';
            }
            return nearestExistingPath(files, prev);
        });
    }, [entityId, files, noFolder]);

    const galleryId = useMemo(() => getGalleryId(scope, currentPath), [scope, currentPath]);
    const currentFolders = useMemo(
        () => (noFolder ? [] : listCurrentFolders(files, currentPath)),
        [files, currentPath, noFolder],
    );
    const currentFiles = useMemo(
        () => (noFolder ? files.filter((item) => !isCloudFolder(item)) : listCurrentFiles(files, currentPath)),
        [files, currentPath, noFolder],
    );

    const applyNextFiles = useCallback(
        async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
            const stat = await onFilesChange(nextFiles);
            if (!stat) showToast(errorMessage, 'danger');
            return stat;
        },
        [onFilesChange, showToast, errorMessage],
    );

    const ensureActiveScope = useCallback(async (): Promise<boolean> => {
        if (getScopeEntityId(scopeRef.current) > 0) return true;
        if (!onEnsureEntity) {
            showToast(saveFirstMessage, 'warning');
            return false;
        }
        setEnsuring(true);
        const ensuredScope = await onEnsureEntity();
        setEnsuring(false);
        if (!ensuredScope) return false;
        scopeRef.current = { ...scopeRef.current, ...ensuredScope };
        if (getScopeEntityId(scopeRef.current) <= 0) {
            showToast(saveFirstMessage, 'warning');
            return false;
        }
        return true;
    }, [onEnsureEntity, saveFirstMessage, showToast]);

    const uploadSelectedFiles = useCallback(
        async (selectedFiles: File[]) => {
            if (!selectedFiles.length) return;

            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) {
                showToast(saveFirstMessage, 'warning');
                return;
            }

            const filesMeta = selectedFiles.map((file) => ({
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                fileSize: file.size,
            }));

            const uploadConfigs = await getCloudflareUploadUrls(filesMeta, activeScope);
            if (!uploadConfigs || uploadConfigs.length !== selectedFiles.length) {
                showToast(errorMessage, 'danger');
                return;
            }

            setProgress(1);
            const uploaded = await uploadFilesWithOverallProgress(selectedFiles, uploadConfigs, setProgress);
            setProgress(false);

            const successFiles = uploaded
                .filter((item) => item.success)
                .map((item) =>
                    toStoredCloudFile({
                        ...item.config,
                        folderPath: noFolder ? undefined : currentPath || undefined,
                    }),
                );
            if (!successFiles.length) {
                showToast(errorMessage, 'danger');
                return;
            }

            const linked = await linkCloudflareFiles(successFiles, activeScope);
            if (!linked) {
                showToast(errorMessage, 'danger');
                return;
            }

            const nextFiles = [...files, ...successFiles];
            const saved = await applyNextFiles(nextFiles);
            if (!saved) return;

            showToast(uploadSuccessMessage, 'success');
        },
        [saveFirstMessage, files, currentPath, noFolder, applyNextFiles, showToast, uploadSuccessMessage, errorMessage],
    );

    const handleChangeFile = useCallback(async () => {
        const selectedFiles = refInputFile.current?.files;
        if (!selectedFiles?.length) return;
        const filesList = Array.from(selectedFiles);
        if (refInputFile.current) refInputFile.current.value = '';
        await uploadSelectedFiles(filesList);
    }, [uploadSelectedFiles]);

    const openFileInput = useCallback(async () => {
        if (!refInputFile.current || isBusy) return;
        if (!(await ensureActiveScope())) return;
        refInputFile.current.value = '';
        refInputFile.current.click();
    }, [isBusy, ensureActiveScope]);

    const handleDeleteFile = useCallback(
        async (file: LeadCloudFileType) => {
            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) return;

            setDeletingKey(file.storageKey);
            const stat = await deleteCloudflareFiles({
                storageKeys: [file.storageKey],
                scope: activeScope,
            });
            if (!stat) {
                setDeletingKey(null);
                showToast(errorMessage, 'danger');
                return;
            }

            const nextFiles = files.filter((item) => item.storageKey !== file.storageKey);
            const saved = await applyNextFiles(nextFiles);
            setDeletingKey(null);
            if (!saved) return;
        },
        [files, applyNextFiles, showToast, errorMessage],
    );

    const createFolder = useCallback(
        async (rawName: string): Promise<boolean> => {
            const name = String(rawName || '').trim();
            if (!isValidFolderName(name)) {
                showToast(folderInvalidNameMessage, 'warning');
                return false;
            }

            const fullPath = joinFolderPath(currentPath, name);
            if (!fullPath || fullPath.length > FOLDER_PATH_MAX) {
                showToast(folderInvalidNameMessage, 'warning');
                return false;
            }

            const taken = siblingNamesAtPath(files, currentPath).some(
                (item) => item.toLocaleLowerCase() === name.toLocaleLowerCase(),
            );
            if (taken) {
                showToast(folderDuplicateMessage, 'warning');
                return false;
            }

            if (!(await ensureActiveScope())) return false;
            const activeScope = scopeRef.current;

            const result = await createCloudflareFolder({
                scope: activeScope,
                parentPath: currentPath,
                name,
            });
            if (!result.stat || !result.folder) {
                if (result.errCode === 'DUPLICATE_NAME') showToast(folderDuplicateMessage, 'warning');
                else if (result.errCode === 'INVALID_NAME' || result.errCode === 'EMPTY_NAME' || result.errCode === 'PATH_TOO_LONG') {
                    showToast(folderInvalidNameMessage, 'warning');
                } else {
                    showToast(errorMessage, 'danger');
                }
                return false;
            }

            const saved = await applyNextFiles([...files, result.folder]);
            if (!saved) return false;
            showToast(folderCreateSuccessMessage, 'success');
            return true;
        },
        [
            currentPath,
            files,
            ensureActiveScope,
            applyNextFiles,
            showToast,
            folderInvalidNameMessage,
            folderDuplicateMessage,
            folderCreateSuccessMessage,
            errorMessage,
        ],
    );

    const handleDeleteFolder = useCallback(
        async (folder: LeadCloudFileType) => {
            const folderPath = String(folder.folderPath || '');
            if (!folderPath) return;
            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) return;

            setDeletingKey(folder.storageKey);
            const stat = await deleteCloudflareFolder({ scope: activeScope, folderPath });
            if (!stat) {
                setDeletingKey(null);
                showToast(errorMessage, 'danger');
                return;
            }

            const nextFiles = files.filter((item) => {
                const path = String(item.folderPath || '');
                return path !== folderPath && !path.startsWith(`${folderPath}/`);
            });
            const saved = await applyNextFiles(nextFiles);
            setDeletingKey(null);
            if (!saved) return;

            if (currentPath === folderPath || currentPath.startsWith(`${folderPath}/`)) {
                setCurrentPath(parentFolderPath(folderPath));
            }
        },
        [files, applyNextFiles, showToast, errorMessage, currentPath],
    );

    const renameItem = useCallback(
        async (item: LeadCloudFileType, rawName: string): Promise<boolean> => {
            const isFolder = isCloudFolder(item);
            const nextName = isFolder ? String(rawName || '').trim() : withLockedExtension(rawName, item.fileName);
            if (!isValidFolderName(nextName) || nextName.length > FILE_NAME_MAX) {
                showToast(folderInvalidNameMessage, 'warning');
                return false;
            }

            if (item.fileName === nextName) return true;

            const parentPath = isFolder ? parentFolderPath(item.folderPath) : String(item.folderPath || '');
            if (isFolder) {
                const fullPath = joinFolderPath(parentPath, nextName);
                if (!fullPath || fullPath.length > FOLDER_PATH_MAX) {
                    showToast(folderInvalidNameMessage, 'warning');
                    return false;
                }
            }

            const taken = siblingNamesAtPath(files, parentPath).some(
                (name) => name !== item.fileName && name.toLocaleLowerCase() === nextName.toLocaleLowerCase(),
            );
            if (taken) {
                showToast(folderDuplicateMessage, 'warning');
                return false;
            }

            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) return false;

            setRenamingKey(item.storageKey);
            try {
                const result = await renameCloudflareItem({
                    scope: activeScope,
                    storageKey: item.storageKey,
                    name: nextName,
                });
                if (!result.stat || !result.items?.length) {
                    if (result.errCode === 'DUPLICATE_NAME') showToast(folderDuplicateMessage, 'warning');
                    else if (result.errCode === 'INVALID_NAME' || result.errCode === 'EMPTY_NAME' || result.errCode === 'PATH_TOO_LONG') {
                        showToast(folderInvalidNameMessage, 'warning');
                    } else {
                        showToast(errorMessage, 'danger');
                    }
                    return false;
                }

                const renamedByKey = new Map(result.items.map((row) => [row.storageKey, row]));
                const nextFiles = files.map((row) => renamedByKey.get(row.storageKey) || row);

                if (isFolder) {
                    const oldPath = String(item.folderPath || '');
                    const newPath = joinFolderPath(parentPath, nextName);
                    if (oldPath && newPath) {
                        setCurrentPath((prev) => replaceFolderPathPrefix(prev, oldPath, newPath));
                    }
                }

                const saved = await applyNextFiles(nextFiles);
                if (!saved) return false;
                showToast(renameSuccessMessage, 'success');
                return true;
            } finally {
                setRenamingKey(null);
            }
        },
        [
            files,
            applyNextFiles,
            showToast,
            folderInvalidNameMessage,
            folderDuplicateMessage,
            renameSuccessMessage,
            errorMessage,
        ],
    );

    const moveItem = useCallback(
        async (item: LeadCloudFileType, rawParentPath: string): Promise<boolean> => {
            const isFolder = isCloudFolder(item);
            const parentPath = normalizeFolderPath(rawParentPath);
            const currentParent = isFolder ? parentFolderPath(item.folderPath) : itemFolderPath(item);
            if (parentPath === currentParent) return true;

            if (isFolder) {
                const oldPath = itemFolderPath(item);
                if (!oldPath || isPathInsideFolder(parentPath, oldPath)) return false;
                const newPath = joinFolderPath(parentPath, item.fileName);
                if (!newPath || newPath.length > FOLDER_PATH_MAX) {
                    showToast(folderInvalidNameMessage, 'warning');
                    return false;
                }
            }

            const taken = siblingNamesAtPath(files, parentPath).some(
                (name) => name.toLocaleLowerCase() === String(item.fileName || '').toLocaleLowerCase(),
            );
            if (taken) {
                showToast(folderDuplicateMessage, 'warning');
                return false;
            }

            const activeScope = scopeRef.current;
            if (getScopeEntityId(activeScope) <= 0) return false;

            setMovingKey(item.storageKey);
            try {
                const result = await moveCloudflareItem({
                    scope: activeScope,
                    storageKey: item.storageKey,
                    parentPath,
                });
                if (!result.stat || !result.items?.length) {
                    if (result.errCode === 'DUPLICATE_NAME') showToast(folderDuplicateMessage, 'warning');
                    else if (result.errCode === 'PATH_TOO_LONG') showToast(folderInvalidNameMessage, 'warning');
                    else showToast(errorMessage, 'danger');
                    return false;
                }

                const movedByKey = new Map(result.items.map((row) => [row.storageKey, row]));
                const nextFiles = files.map((row) => movedByKey.get(row.storageKey) || row);

                if (isFolder) {
                    const oldPath = itemFolderPath(item);
                    const newPath = joinFolderPath(parentPath, item.fileName);
                    if (oldPath && newPath) {
                        setCurrentPath((prev) => replaceFolderPathPrefix(prev, oldPath, newPath));
                    }
                }

                const saved = await applyNextFiles(nextFiles);
                if (!saved) return false;
                showToast(moveSuccessMessage, 'success');
                return true;
            } finally {
                setMovingKey(null);
            }
        },
        [
            files,
            applyNextFiles,
            showToast,
            folderInvalidNameMessage,
            folderDuplicateMessage,
            moveSuccessMessage,
            errorMessage,
        ],
    );

    const enterFolder = useCallback((folder: LeadCloudFileType) => {
        const path = String(folder.folderPath || '');
        if (path) setCurrentPath(path);
    }, []);

    return {
        galleryId,
        progress,
        deletingKey,
        renamingKey,
        movingKey,
        isBusy,
        currentPath,
        currentFolders,
        currentFiles,
        setCurrentPath,
        enterFolder,
        refInputFile,
        handleChangeFile,
        openFileInput,
        handleDeleteFile,
        createFolder,
        handleDeleteFolder,
        renameItem,
        moveItem,
    };
};

export default useFilesContainer;
